import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  Stack, TextField, Tooltip, Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { PageContainer } from '../../components/AdminContentWrapper';
import { ADMIN_COLORS, containedButtonSx, dangerButtonSx, outlinedButtonSx, panelSx } from '../../components/adminStyles';
import ConfirmDestructiveDialog from '../../../../components/common/ConfirmDestructiveDialog';
import DashboardView from '../../../../components/dashboard/DashboardView';
import DashboardsService, { withResults } from '../../../../services/DashboardsService';
import { StatusChip, formatDate } from './MyDashboards';

export const POLL_INTERVAL_MS = 3000;
const POLL_MAX_MS = 6 * 60 * 1000;

/** Edición del diseño: título, descripción y mosaicos (orden, título, quitar). */
export const EditDashboardDialog = ({ open, dashboard, onClose, onSave }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tiles, setTiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && dashboard) {
      setTitle(dashboard.title || '');
      setDescription(dashboard.description || '');
      setTiles((dashboard.tiles || []).map((tile) => ({ id: tile.id, title: tile.title, type: tile.type })));
      setError(null);
    }
  }, [open, dashboard]);

  const move = (index, delta) => setTiles((prev) => {
    const next = [...prev];
    const [tile] = next.splice(index, 1);
    next.splice(index + delta, 0, tile);
    return next;
  });

  const invalid = !title.trim() || tiles.length === 0 || tiles.some((tile) => !tile.title.trim());

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave({ title: title.trim(), description: description.trim(), tiles: tiles.map(({ id, title: t }) => ({ id, title: t.trim() })) });
    } catch (err) {
      setError(err.message || 'No se pudieron guardar los cambios.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Editar tablero</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField label="Título" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth size="small" error={!title.trim()} />
          <TextField label="Descripción" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth size="small" multiline minRows={2} />
          <Typography variant="subtitle2">Mosaicos ({tiles.length})</Typography>
          {tiles.map((tile, index) => (
            <Stack key={tile.id} direction="row" spacing={1} alignItems="center">
              <TextField
                value={tile.title}
                onChange={(e) => setTiles((prev) => prev.map((t) => (t.id === tile.id ? { ...t, title: e.target.value } : t)))}
                size="small"
                fullWidth
                inputProps={{ 'aria-label': `Título del mosaico ${index + 1}` }}
                error={!tile.title.trim()}
              />
              <Tooltip title="Subir"><span>
                <IconButton size="small" disabled={index === 0} onClick={() => move(index, -1)} aria-label={`Subir ${tile.title}`}><ArrowUpwardIcon fontSize="small" /></IconButton>
              </span></Tooltip>
              <Tooltip title="Bajar"><span>
                <IconButton size="small" disabled={index === tiles.length - 1} onClick={() => move(index, 1)} aria-label={`Bajar ${tile.title}`}><ArrowDownwardIcon fontSize="small" /></IconButton>
              </span></Tooltip>
              <Tooltip title="Quitar del tablero"><span>
                <IconButton size="small" disabled={tiles.length === 1} onClick={() => setTiles((prev) => prev.filter((t) => t.id !== tile.id))} aria-label={`Quitar ${tile.title}`}><CloseIcon fontSize="small" /></IconButton>
              </span></Tooltip>
            </Stack>
          ))}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={invalid || saving} startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>
          {saving ? 'Guardando…' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Un tablero guardado: últimos resultados, refresco sin IA, edición y borrado. */
const DashboardDetail = ({ basePath = '/admin/home/tableros' }) => {
  const { dashboardId } = useParams();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await DashboardsService.get(dashboardId);
      setDashboard(data);
      setError(null);
      return data;
    } catch (err) {
      setError(err.status === 404 ? 'Este tablero no existe o ya fue borrado.' : (err.message || 'No se pudo cargar el tablero.'));
      return null;
    }
  }, [dashboardId]);

  const stopPolling = () => {
    if (pollRef.current) clearTimeout(pollRef.current);
    pollRef.current = null;
  };

  const pollRun = useCallback((runId, startedAt) => {
    pollRef.current = setTimeout(async () => {
      try {
        const run = await DashboardsService.getRun(dashboardId, runId);
        if (run.status === 'running' && Date.now() - startedAt < POLL_MAX_MS) {
          pollRun(runId, startedAt);
          return;
        }
        setRefreshing(false);
        await load();
        if (run.status === 'running') {
          setNotice({ severity: 'warning', text: 'El refresco sigue en curso; vuelve a revisar en unos minutos.' });
        } else if (run.status === 'failed') {
          setNotice({ severity: 'error', text: run.message || 'No se pudo refrescar el tablero.' });
        } else {
          setNotice({ severity: run.status === 'partial' ? 'warning' : 'success',
            text: run.status === 'partial' ? 'Tablero actualizado; algunos mosaicos tienen error.' : 'Tablero actualizado.' });
        }
      } catch (err) {
        setRefreshing(false);
        setNotice({ severity: 'error', text: err.message || 'No se pudo consultar el refresco.' });
      }
    }, POLL_INTERVAL_MS);
  }, [dashboardId, load]);

  useEffect(() => {
    load().then((data) => {
      // Si quedó un refresco en curso (otra pestaña, recarga), seguirlo.
      if (data?.last_run_status === 'running' && data.last_run_id) {
        setRefreshing(true);
        pollRun(data.last_run_id, Date.now());
      }
    });
    return stopPolling;
  }, [load, pollRun]);

  const handleRefresh = async () => {
    setNotice(null);
    setRefreshing(true);
    try {
      const { run_id: runId } = await DashboardsService.refresh(dashboardId);
      pollRun(runId, Date.now());
    } catch (err) {
      setRefreshing(false);
      setNotice({ severity: 'error', text: err.message || 'No se pudo iniciar el refresco.' });
    }
  };

  const handleSaveEdit = async (changes) => {
    await DashboardsService.update(dashboardId, changes);
    setEditOpen(false);
    await load();
    setNotice({ severity: 'success', text: 'Cambios guardados.' });
  };

  const handleDelete = async () => {
    await DashboardsService.remove(dashboardId);
    navigate(basePath);
  };

  const actions = (
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
      <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(basePath)} sx={outlinedButtonSx}>
        Mis tableros
      </Button>
      <Button
        variant="contained"
        startIcon={refreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
        onClick={handleRefresh}
        disabled={!dashboard || refreshing}
        sx={containedButtonSx}
      >
        {refreshing ? 'Refrescando…' : 'Refrescar'}
      </Button>
    </Stack>
  );

  return (
    <PageContainer title={dashboard?.title || 'Tablero'} description={dashboard?.description} actions={actions}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {notice && <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice.text}</Alert>}
      {!dashboard && !error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: ADMIN_COLORS.accent }} /></Box>
      )}
      {dashboard && (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
            <StatusChip status={refreshing ? 'running' : dashboard.last_run_status} />
            <Typography variant="body2" sx={{ color: ADMIN_COLORS.muted, flexGrow: 1 }}>
              {dashboard.refreshed_at ? `Datos del ${formatDate(dashboard.refreshed_at)}` : 'Aún sin datos: pulsa Refrescar.'}
              {dashboard.source_question ? ` · Pregunta original: "${dashboard.source_question}"` : ''}
            </Typography>
            <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => setEditOpen(true)} sx={outlinedButtonSx}>Editar</Button>
            <Button size="small" variant="outlined" startIcon={<DeleteOutlineIcon />} onClick={() => setDeleteOpen(true)} sx={dangerButtonSx}>Borrar</Button>
          </Stack>
          {dashboard.last_run?.status === 'failed' && !refreshing && (
            <Alert severity="error" sx={{ mb: 2 }}>Último refresco fallido: {dashboard.last_run.message}</Alert>
          )}
          <Box sx={{ ...panelSx, p: { xs: 1.5, md: 2 } }}>
            <DashboardView dashboard={withResults({ ...dashboard, warnings: [] }, dashboard.results)} hideHeader />
          </Box>
          <EditDashboardDialog open={editOpen} dashboard={dashboard} onClose={() => setEditOpen(false)} onSave={handleSaveEdit} />
          <ConfirmDestructiveDialog
            open={deleteOpen}
            title="Borrar tablero"
            onClose={() => setDeleteOpen(false)}
            onConfirm={handleDelete}
            progressMessage="Borrando el tablero…"
          >
            <Typography variant="body1">¿Borrar el tablero <strong>{dashboard.title}</strong>?</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Se borran su diseño y sus últimos datos. La conversación donde se generó no cambia.
            </Typography>
          </ConfirmDestructiveDialog>
        </>
      )}
    </PageContainer>
  );
};

export default DashboardDetail;
